import type { EvaluationOutcome } from "@/types";

/**
 * Which rubric the screens compare on, and how its scores read.
 *
 * Rubric v2 scores **0.0–1.0** — a share of criteria met, shown as a share
 * (be/RUBRIC.md R2.7). v1 scored 1–5 and is not carried on (R10): a v1 row is
 * shown for what it is, and never averaged with, compared against or coloured
 * on the v2 scale. The same goes for a v2 row whose evaluation policy is not
 * `current` — R3.9 keeps `legacy` and `unknown` out of every aggregate.
 */
export const CURRENT_RUBRIC = "v2";

/**
 * The common rubric (BE `common_v1`, be/RUBRIC.md R16): every TC on the same
 * criteria, 0–1. A Batch Test run scored with it is its own measure — named as
 * the common rubric, never lumped in with "another rubric", and averaged only
 * with other common scores.
 */
export const COMMON_RUBRIC = "common_v1";

/**
 * The cell buckets on 0–1: v1's 4.0 / 3.0 as the same share of full marks
 * (4/5 · 3/5). Decided 2026-09-29 (R14.3-㉬) — the spec left the value open.
 */
export const SCORE_BUCKETS = { hi: 0.8, mid: 0.6 } as const;

export type ScoreBucket = "hi" | "mid" | "lo";

/** Sequential buckets over a comparable (v2) score. */
export function scoreBucket(score: number): ScoreBucket {
  if (score >= SCORE_BUCKETS.hi) return "hi";
  if (score >= SCORE_BUCKETS.mid) return "mid";
  return "lo";
}

type Scored = Extract<EvaluationOutcome, { status: "scored" }>;

/**
 * Whether this score belongs with the others — current rubric, current
 * policy. A v2 row from a BE that does not send the policy yet counts as
 * current: every v2 row the scorer writes today is.
 */
export function isComparable(outcome: Scored): boolean {
  return (
    outcome.rubric_version === CURRENT_RUBRIC &&
    (outcome.evaluation_policy == null || outcome.evaluation_policy === "current")
  );
}

export function isCommon(outcome: Scored): boolean {
  return outcome.rubric_version === COMMON_RUBRIC;
}

/** What an incomparable score is tagged with — its rubric, or its policy. */
export function incomparableTag(outcome: Scored): string {
  if (isCommon(outcome)) return "common rubric";
  if (outcome.evaluation_policy === "reviewed_criteria") return "확정 기준";
  if (outcome.rubric_version !== CURRENT_RUBRIC) return outcome.rubric_version ?? "rubric ?";
  return outcome.evaluation_policy ?? "unknown";
}

export function incomparableReason(outcome: Scored): string {
  if (isCommon(outcome)) {
    return `common rubric 점수 — TC 와 무관한 공통 기준(0~1)입니다. rubric ${CURRENT_RUBRIC} 점수와 섞지 않고 common 끼리만 평균합니다`;
  }
  return `${incomparableTag(outcome)} 점수 — 지금 눈금(rubric ${CURRENT_RUBRIC}, 0~1)과 같은 지표가 아니라 평균·비교에서 뺍니다`;
}

/**
 * Scores are compared down a column, so they keep a fixed shape: always two
 * decimals — 0.85 beside 0.80, never 0.8.
 */
export function formatScore(score: number): string {
  return score.toFixed(2);
}

/**
 * Under this a change is noise, not an improvement. The artifact's ±0.02 on
 * v1's four-point range, carried to v2's 0–1 as the same share (0.02 / 4) —
 * which is also what two decimals already print as 0.00.
 */
export const DELTA_EPSILON = 0.005;

/** `+0.10` / `-0.05` / `±0.00` — a score change always carries its sign. */
export function formatDelta(delta: number): string {
  if (Math.abs(delta) <= DELTA_EPSILON) return "±0.00";
  return `${delta > 0 ? "+" : ""}${delta.toFixed(2)}`;
}
